/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
export const b2cPolicies = {
    names: {
        signUpSignIn: 'B2C_1A_SYNNEX_SIGNIN'
    },
    authorities: {
        signUpSignIn: {
            authority: 'https://newcloudsynnex.b2clogin.com/newcloudsynnex.onmicrosoft.com/B2C_1A_SYNNEX_SIGNIN',
        }
    },
    authorityDomain: 'newcloudsynnex.b2clogin.com',
};


/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const msalConfig = {
    auth: {
        clientId: 'c4664a55-4337-40a1-a584-ee7c0579b560', // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: '/', // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'localStorage', // Configures cache location. "localStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {
        loggerOptions: {
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        // console.error(message);
                        return;
                    case LogLevel.Info:
                        // console.info(message);
                        return;
                    case LogLevel.Verbose:
                        // console.debug(message);
                        return;
                    case LogLevel.Warning:
                        // console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    apiTodoList: {
        endpoint: 'http://localhost:5000/',
        scopes: {
            read: ['https://newcloudsynnex.onmicrosoft.com/tasks-api/tasks.read'],
            write: ['https://newcloudsynnex.onmicrosoft.com/tasks-api/tasks.write'],
        },
    },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: [...protectedResources.apiTodoList.scopes.read, ...protectedResources.apiTodoList.scopes.write],
};


/* access_token": "eyJhbGciOiJSUzI1NiIsImtpZCI6Ilg1ZVhrNHh5b2pORnVtMWtsMll0djhkbE5QNC1jNTdkTzZRR1RWQndhTmsiLCJ0eXAiOiJKV1QifQ.eyJvaWQiOiIyY2ZjOTg4MC04MzA0LTRlNTAtYTNmYy0yZmI5YzZiN2RlZmYiLCJzdWIiOiIyY2ZjOTg4MC04MzA0LTRlNTAtYTNmYy0yZmI5YzZiN2RlZmYiLCJuYW1lIjoiU3lubmV4Q2xvdWQiLCJnaXZlbl9uYW1lIjoiU3lubmV4Q2xvdWQiLCJmYW1pbHlfbmFtZSI6IlBWVCBMVEQiLCJ0ZnAiOiJCMkNfMV9zaSIsIm5vbmNlIjoiN2U4MzY2MzktN2Y5NC00NDhiLTgxZDItYjY1YTk3YTcwMjRiIiwic2NwIjoidGFza3Mud3JpdGUgdGFza3MucmVhZCIsImF6cCI6IjI4MDViMjY0LTNiYTYtNDdlMS1hMzE3LTY5NzcyYjZlZjJjMCIsInZlciI6IjEuMCIsImlhdCI6MTcwMDcyNjA4MiwiYXVkIjoiY2ZlMTA5NjEtOTRhNS00MzMxLTk1N2EtOTUzMzRhZmEyN2YzIiwiZXhwIjoxNzAwNzI5NjgyLCJpc3MiOiJodHRwczovL3N0Z3N5bm5leHBhcnRuZXIuYjJjbG9naW4uY29tLzI0MzJiMWIyLWMyOGMtNGEyMi05YjI0LTVjOTY2ZmEwZjAwNS92Mi4wLyIsIm5iZiI6MTcwMDcyNjA4Mn0.IkIa9oV_nl5ONUklrXfCTIdtr_BbIq0dAz1CkmkEv3fQ0dNz83DMUkWhF5x30HjnORxK5pUnnkRx7Nv9hw2hN--fPngdkyWTjaG4dAfgbdLBaGhd9YjDlSPvKWx1PgdR9jeIH91NUMnYYaJbKOGro_iDWLwOTOgnMm62gEhLKq2vsnDYb5src3ZnWRMVflETGr7QXY8cHbIfWhRLEOTXPhzr3SI9OdaevZr4hcn0EWr3Ny2EDOEOfZHQvZYvL3S0XT6nJV3HYVudl-sGZ0o-FlbsQIcUaVsTrxpMnPTo_zCQS2rg5gaujLLi54Hsi9hL2SvcgvRG-HvzYwVResbumA",

    "id_token": "eyJhbGciOiJSUzI1NiIsImtpZCI6Ilg1ZVhrNHh5b2pORnVtMWtsMll0djhkbE5QNC1jNTdkTzZRR1RWQndhTmsiLCJ0eXAiOiJKV1QifQ.eyJ2ZXIiOiIxLjAiLCJpc3MiOiJodHRwczovL3N0Z3N5bm5leHBhcnRuZXIuYjJjbG9naW4uY29tLzI0MzJiMWIyLWMyOGMtNGEyMi05YjI0LTVjOTY2ZmEwZjAwNS92Mi4wLyIsInN1YiI6IjJjZmM5ODgwLTgzMDQtNGU1MC1hM2ZjLTJmYjljNmI3ZGVmZiIsImF1ZCI6IjI4MDViMjY0LTNiYTYtNDdlMS1hMzE3LTY5NzcyYjZlZjJjMCIsImV4cCI6MTcwMDcyOTY4Miwibm9uY2UiOiI3ZTgzNjYzOS03Zjk0LTQ0OGItODFkMi1iNjVhOTdhNzAyNGIiLCJpYXQiOjE3MDA3MjYwODIsImF1dGhfdGltZSI6MTcwMDcyNjA4MCwib2lkIjoiMmNmYzk4ODAtODMwNC00ZTUwLWEzZmMtMmZiOWM2YjdkZWZmIiwibmFtZSI6IlN5bm5leENsb3VkIiwiZ2l2ZW5fbmFtZSI6IlN5bm5leENsb3VkIiwiZmFtaWx5X25hbWUiOiJQVlQgTFREIiwidGZwIjoiQjJDXzFfc2kiLCJhdF9oYXNoIjoiUlpkVlo4WEVkUXlUZXdJV1o4emFrQSIsIm5iZiI6MTcwMDcyNjA4Mn0.hS9u76iN6kMQLlv7WifFOVhoBkEXJJCs8kw9odJU_jMlP3CF7BHHLY21EbDWG547bF9lvi3te1Lt-6_wFbW2VIrzpGGgeZ5lVQZmTu-XmWx8zXQ7zCUwz1KqMeqceHwfLjAnRydiS9OQv9jhV24CQxskVis6mnaStUJXO2JHHIwIhK7j4tV_gIYaKmbVlCXi6JQjLnMGlJjGdKdBRgmKiYbcVZxL7apKZMCSD34zBvcElrqhtEY4cFBcv7aCjOlCTxoSeu6_mKPixn79rZRacFkAXFkhsB3rwkXiyI11CTGyyxmfTJgWksk6bl3Mzz8I6c566mpHcbx18Ubi95VObg",
    */